import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Chip, Stack, Divider, Alert,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, TextField,
  CircularProgress, Collapse, Tooltip, IconButton,
} from '@mui/material';
import {
  AutoAwesome, CheckCircle, SkipNext, Refresh, Tune, ArrowBack,
  ExpandMore, ExpandLess, PlayArrow, Save,
} from '@mui/icons-material';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';
import {
  GAME_TYPES, DIFFICULTIES, DOMAIN_NAMES, ROUTES,
} from '../../config/shared_config';
import { buildGenerationParameters } from '../../utils/taxonomyExerciseWorkflow';
import { drawStudioExercise, buildNotionPool } from '../../utils/exerciseStudioPerimeter';
import ExerciseContentVisualEditor from '../../components/admin/ExerciseContentVisualEditor';
import ExerciseValidationPanel from '../../components/admin/ExerciseValidationPanel';
import ExercisePreview from '../../components/admin/ExercisePreview';
import JSONEditor from '../../components/admin/JSONEditor';
import GenerationLogPanel from '../../components/admin/GenerationLogPanel';

const SCHOOL_LEVELS = ['CP', 'CE1', 'CE2', 'CM1', 'CM2', '6ème'];

const DEFAULT_PERIMETER = {
  gameTypes: ['speed_game'],
  domains: ['orthographe'],
  schoolLevel: 'CE2',
  difficulty: 'medium',
};

export default function ExerciseStudioScreen() {
  const navigate = useNavigate();

  const [perimeter, setPerimeter] = useState(DEFAULT_PERIMETER);
  const [started, setStarted] = useState(false);

  const [draw, setDraw] = useState(null);
  const [exercise, setExercise] = useState(null); // { gameType, parameters, content, title }
  const [validation, setValidation] = useState(null);

  const [trace, setTrace] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showJson, setShowJson] = useState(false);
  const [correctingItemKey, setCorrectingItemKey] = useState(null);
  const [sessionStats, setSessionStats] = useState({ validated: 0, skipped: 0, created: 0 });

  const gameTypeList = useMemo(() => Object.values(GAME_TYPES || {}), []);
  const difficultyList = useMemo(() => Object.values(DIFFICULTIES || {}), []);
  const domainList = useMemo(() => Object.entries(DOMAIN_NAMES || {}), []);

  const poolSize = useMemo(
    () => buildNotionPool(perimeter.domains, perimeter.schoolLevel).length,
    [perimeter.domains, perimeter.schoolLevel],
  );

  const canStart = perimeter.gameTypes.length > 0
    && perimeter.domains.length > 0
    && poolSize > 0;

  const updatePerimeter = (field, value) => {
    setPerimeter((prev) => ({ ...prev, [field]: value }));
  };

  /** Génère le contenu LLM pour un tirage donné (sans l'enregistrer). */
  const generateForDraw = useCallback(async (currentDraw) => {
    setGenerating(true);
    setErrorMessage('');
    setValidation(null);
    setTrace(null);
    try {
      const parameters = buildGenerationParameters(
        { domain: currentDraw.domain, competence: currentDraw.competence, notion: currentDraw.notion },
        { schoolLevel: perimeter.schoolLevel, difficulty: perimeter.difficulty, gameType: currentDraw.gameType },
      );
      const result = await AdminService.generateExercise({
        gameType: currentDraw.gameType,
        parameters,
      });
      if (!result?.generatedContent) {
        throw new Error('Contenu généré vide');
      }
      setExercise({
        gameType: currentDraw.gameType,
        parameters: result.parameters || parameters,
        content: result.generatedContent,
        title: `${currentDraw.notionLabel} — ${currentDraw.gameTypeLabel}`,
      });
      setValidation(result.validation || null);
      setTrace(result.trace || null);
    } catch (error) {
      console.error('Erreur génération atelier:', error);
      setErrorMessage(error.message || "Erreur lors de la génération de l'exercice");
      setExercise(null);
    } finally {
      setGenerating(false);
    }
  }, [perimeter.schoolLevel, perimeter.difficulty]);

  /** Tire un nouvel exercice dans le périmètre et génère son contenu. */
  const drawAndGenerate = useCallback(async () => {
    const next = drawStudioExercise(perimeter, draw?.signature);
    if (!next) {
      setErrorMessage('Le périmètre ne couvre aucune notion — ajustez les domaines ou le niveau.');
      return;
    }
    setDraw(next);
    setExercise(null);
    await generateForDraw(next);
  }, [perimeter, draw?.signature, generateForDraw]);

  const handleStart = async () => {
    if (!canStart) return;
    setStarted(true);
    setSessionStats({ validated: 0, skipped: 0, created: 0 });
    await drawAndGenerate();
  };

  const handleRegenerate = async () => {
    if (!draw || generating) return;
    setExercise(null);
    await generateForDraw(draw);
  };

  const handleSkip = async () => {
    if (generating) return;
    setSessionStats((s) => ({ ...s, skipped: s.skipped + 1 }));
    await drawAndGenerate();
  };

  const persistExercise = async (validationStatus) => {
    if (!exercise?.content || Object.keys(exercise.content).length === 0) {
      setErrorMessage('Aucun contenu à enregistrer.');
      return null;
    }
    setSaving(true);
    setErrorMessage('');
    try {
      const created = await AdminService.createExercise({
        type: 'llm',
        source: 'llm',
        gameType: exercise.gameType,
        parameters: exercise.parameters,
        content: exercise.content,
        title: exercise.title?.trim() || `${draw.notionLabel} — ${draw.gameTypeLabel}`,
        description: '',
        isActive: validationStatus === 'valide',
        validationStatus,
      });
      return created || {};
    } catch (error) {
      console.error('Erreur enregistrement exercice:', error);
      setErrorMessage(error.message || "Erreur lors de l'enregistrement de l'exercice");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleValidateAndNext = async () => {
    const created = await persistExercise('valide');
    if (!created) return;
    // Un speed_game / word_lists « pool » est découpé en N sous-exercices atomiques au save.
    const atomic = created.atomicCount || 1;
    setSessionStats((s) => ({ ...s, validated: s.validated + 1, created: s.created + atomic }));
    await drawAndGenerate();
  };

  const handleSaveDraftAndNext = async () => {
    const created = await persistExercise('nouveau');
    if (!created) return;
    setSessionStats((s) => ({ ...s, created: s.created + (created.atomicCount || 1) }));
    await drawAndGenerate();
  };

  const handleContentChange = (content) => {
    setExercise((prev) => (prev ? { ...prev, content } : prev));
    setValidation(null);
  };

  const handleCorrectItemWithLLM = async (arrayKey, index) => {
    if (!exercise) return;
    const itemKey = `${arrayKey}-${index}`;
    setCorrectingItemKey(itemKey);
    setErrorMessage('');
    try {
      const correctedItem = await AdminService.correctExerciseItemWithLLM({
        gameType: exercise.gameType,
        content: exercise.content,
        parameters: exercise.parameters,
        arrayKey,
        itemIndex: index,
      });
      const items = [...(exercise.content?.[arrayKey] || [])];
      items[index] = correctedItem;
      handleContentChange({ ...exercise.content, [arrayKey]: items });
    } catch (error) {
      console.error('Erreur correction LLM élément:', error);
      setErrorMessage(error.message || "Erreur lors de la correction de l'élément");
    } finally {
      setCorrectingItemKey(null);
    }
  };

  const renderPerimeterForm = () => (
    <Paper sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h6" gutterBottom>Périmètre de génération</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Définissez le périmètre : à chaque exercice, un type et une notion seront tirés
        au hasard dans ce périmètre. Vous générez, relisez, validez, et passez au suivant.
      </Typography>

      <Stack spacing={2.5}>
        <FormControl fullWidth>
          <InputLabel>Types d&apos;exercices</InputLabel>
          <Select
            multiple
            value={perimeter.gameTypes}
            onChange={(e) => updatePerimeter('gameTypes', e.target.value)}
            input={<OutlinedInput label="Types d'exercices" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => (
                  <Chip
                    key={id}
                    size="small"
                    label={gameTypeList.find((g) => g.id === id)?.label || id}
                  />
                ))}
              </Box>
            )}
          >
            {gameTypeList.map((game) => (
              <MenuItem key={game.id} value={game.id}>{game.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Domaines</InputLabel>
          <Select
            multiple
            value={perimeter.domains}
            onChange={(e) => updatePerimeter('domains', e.target.value)}
            input={<OutlinedInput label="Domaines" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => (
                  <Chip key={id} size="small" label={DOMAIN_NAMES[id] || id} />
                ))}
              </Box>
            )}
          >
            {domainList.map(([id, label]) => (
              <MenuItem key={id} value={id}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Niveau</InputLabel>
            <Select
              value={perimeter.schoolLevel}
              label="Niveau"
              onChange={(e) => updatePerimeter('schoolLevel', e.target.value)}
            >
              {SCHOOL_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>{level}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Difficulté</InputLabel>
            <Select
              value={perimeter.difficulty}
              label="Difficulté"
              onChange={(e) => updatePerimeter('difficulty', e.target.value)}
            >
              {difficultyList.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Alert severity={poolSize > 0 ? 'info' : 'warning'}>
          {poolSize > 0
            ? `${poolSize} notion${poolSize > 1 ? 's' : ''} couverte${poolSize > 1 ? 's' : ''} × ${perimeter.gameTypes.length} type${perimeter.gameTypes.length > 1 ? 's' : ''} dans ce périmètre.`
            : 'Aucune notion couverte par ces domaines à ce niveau — ajustez la sélection.'}
        </Alert>

        <Button
          variant="contained"
          size="large"
          startIcon={<PlayArrow />}
          onClick={handleStart}
          disabled={!canStart}
        >
          Démarrer l&apos;atelier
        </Button>
      </Stack>
    </Paper>
  );

  const renderDrawHeader = () => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip color="secondary" size="small" label={draw?.gameTypeLabel} />
          <Chip
            size="small"
            variant="outlined"
            sx={{ fontFamily: 'monospace' }}
            label={`${draw?.domain} / ${draw?.competence} / ${draw?.notion}`}
          />
          <Chip size="small" variant="outlined" label={perimeter.schoolLevel} />
          <Chip
            size="small"
            variant="outlined"
            label={difficultyList.find((d) => d.id === perimeter.difficulty)?.label || perimeter.difficulty}
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Exercices validés (un speed_game / word_lists est découpé en plusieurs sous-exercices)">
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={`Validés : ${sessionStats.validated}${sessionStats.created > sessionStats.validated ? ` (${sessionStats.created} sous-ex.)` : ''}`}
            />
          </Tooltip>
          <Chip size="small" variant="outlined" label={`Passés : ${sessionStats.skipped}`} />
          <Tooltip title="Régénérer le contenu (même notion / même type)">
            <span>
              <IconButton size="small" onClick={handleRegenerate} disabled={generating || saving}>
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small"
            startIcon={<Tune />}
            onClick={() => setStarted(false)}
            disabled={generating || saving}
          >
            Périmètre
          </Button>
        </Stack>
      </Box>
      {draw?.associationWarning && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>{draw.associationWarning}</Alert>
      )}
    </Paper>
  );

  const hasContent = exercise?.content && Object.keys(exercise.content).length > 0;

  const renderAtelier = () => (
    <>
      {renderDrawHeader()}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {generating ? (
        <Paper sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">Génération de l&apos;exercice…</Typography>
        </Paper>
      ) : exercise ? (
        <Paper sx={{ p: 3, pb: 12 }}>
          <TextField
            fullWidth
            size="small"
            label="Titre"
            value={exercise.title || ''}
            onChange={(e) => setExercise((prev) => ({ ...prev, title: e.target.value }))}
            sx={{ mb: 2 }}
          />

          {validation && (
            <Box sx={{ mb: 2 }}>
              <ExerciseValidationPanel validation={validation} />
            </Box>
          )}

          {exercise.gameType && (
            <ExerciseContentVisualEditor
              gameType={exercise.gameType}
              content={exercise.content}
              onChange={handleContentChange}
              onCorrectItem={handleCorrectItemWithLLM}
              correctingItemKey={correctingItemKey}
            />
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setShowJson((v) => !v)}
              endIcon={showJson ? <ExpandLess /> : <ExpandMore />}
            >
              Éditeur JSON (avancé)
            </Button>
            <Collapse in={showJson}>
              <Box sx={{ mt: 1 }}>
                <JSONEditor
                  value={exercise.content}
                  onChange={handleContentChange}
                  height="320px"
                />
              </Box>
            </Collapse>
          </Box>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Aperçu</Typography>
          <ExercisePreview gameType={exercise.gameType} content={exercise.content} />
        </Paper>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Aucun contenu généré.
          </Typography>
          <Button variant="contained" startIcon={<AutoAwesome />} onClick={handleRegenerate}>
            Générer
          </Button>
        </Paper>
      )}

      {!generating && <GenerationLogPanel trace={trace} />}

      {/* Barre d'actions fixe */}
      {exercise && !generating && (
        <Paper
          elevation={6}
          sx={{
            position: 'sticky', bottom: 0, mt: 2, p: 1.5,
            display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap',
            zIndex: 5,
          }}
        >
          <Button
            startIcon={<SkipNext />}
            onClick={handleSkip}
            disabled={saving}
          >
            Passer
          </Button>
          <Button
            variant="outlined"
            startIcon={<Save />}
            onClick={handleSaveDraftAndNext}
            disabled={saving || !hasContent}
          >
            Brouillon &amp; suivant
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}
            onClick={handleValidateAndNext}
            disabled={saving || !hasContent}
          >
            Valider &amp; suivant
          </Button>
        </Paper>
      )}
    </>
  );

  return (
    <AdminLayout>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(ROUTES.ADMIN.EXERCISES.LIST)} sx={{ mr: 2 }}>
            Liste
          </Button>
          <Typography variant="h5">Atelier de génération</Typography>
        </Box>
      </Box>

      {started ? renderAtelier() : renderPerimeterForm()}
    </AdminLayout>
  );
}
